// PDP Gallery Apparel — scroll-driven triptych transition.
// Standalone mode: 200vh section; center image shrinks, side panels slide in.
// Integrated mode (data-integrated="true"): two-phase scroll — product info fades out,
// then triptych animates. After scroll, handle-control and drag cycle images.

(() => {
  const TRANSITION_MS = 600;
  const SNAP_DURATION = 300;
  const DEAD_ZONE = 10;

  const LAYOUTS = {
    center: {
      start: { left: 0, top: 0, width: 100, height: 100, opacity: 1 },
      end:   { left: 30, top: 5, width: 40, height: 90, opacity: 1 }
    },
    left: {
      start: { left: -30, top: 17.5, width: 27, height: 65, opacity: 0 },
      end:   { left: 1.5, top: 17.5, width: 27, height: 65, opacity: 1 }
    },
    right: {
      start: { left: 110, top: 17.5, width: 27, height: 65, opacity: 0 },
      end:   { left: 71.5, top: 17.5, width: 27, height: 65, opacity: 1 }
    }
  };

  function lerp(a, b, t) { return a + (b - a) * t; }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  class PdpGalleryApparel extends HTMLElement {

    connectedCallback() {
      this._init();
    }

    _init() {
      // The host is rendered with inline `opacity:0` (in the Liquid markup) so the
      // items never paint UNpositioned in the gap between first paint and this
      // deferred script upgrading the element. _renderPhase1/_renderAtProgress below
      // position items synchronously, then _reveal() clears the inline opacity in the
      // same tick — no flash on load or on the (synchronous) variant-swap re-init.
      if (!this.offsetWidth) { this._reveal(); return; }

      this.items = Array.from(this.querySelectorAll('.pdp-gallery-apparel__item'));
      if (window.innerWidth < 1025) {
        this.items = this.items.filter(item => !item.hasAttribute('data-hero'));
      }
      if (this.items.length < 1) { this._reveal(); return; }

      this.viewport = this.querySelector('.pdp-gallery-apparel__viewport');
      this.stage = this.querySelector('.pdp-gallery-apparel__stage');
      this.handleEl = this.querySelector('handle-control');

      this.centerIndex = 0;
      this.isAnimating = false;
      this._scrollComplete = false;
      this._rafId = null;
      this._dragActive = false;
      this._dragScrollPos = 0;
      this._dragHandleDisp = 0;
      this._dragRAFId = null;
      this._snapDragRAF = null;
      this._dragCloneMap = null;

      this._integrated = this.dataset.integrated === 'true';

      if (this._integrated) {
        const productRow = this.closest('.product');
        this._infoWrapper = productRow ? productRow.querySelector('.product__info-wrapper') : null;
        this._breadcrumbSection = document.querySelector('.breadcrumbs-section');
      }

      this._roleMap = {};
      for (const item of this.items) {
        this._roleMap[item.dataset.role] = item;
      }

      /* Window/document listeners ride this controller and die with the
       * element — variant swaps replace the gallery via innerHTML, and the
       * old element's scroll/resize handlers used to leak (benign early
       * return, but one more pair per colour switch, forever). */
      this._ac = new AbortController();

      if (window.innerWidth < 1025) {
        this._initMobileCarousel();
        this._reveal();
        return;
      }

      this._bindScroll();
      this._bindHandleControl();
      this._bindTouch();
      this._bindKeyboard();

      if (this._integrated) {
        document.body.classList.add('has-triptych-gallery');
        this._renderPhase1();
        /* A variant media swap re-connects a FRESH element mid-scroll. Blind
         * phase-1 above (full-bleed center in the fixed viewport) is only the
         * scroll-top state — left as-is it flashes a gallery image over
         * whatever the user is actually looking at until the next scroll
         * event. Re-derive the real state (progress render, is-phase2 /
         * is-past classes, info-wrapper alpha, nav mode via
         * _onScrollComplete) from the current scroll position now,
         * synchronously — same task, so the wrong frame never paints. */
        this._updateScroll();
      } else {
        this._renderAtProgress(0);
      }
      // Positions are applied synchronously above — safe to show now.
      this._reveal();
    }

    // Clear the parse-time inline opacity:0 once items are positioned.
    _reveal() {
      this.style.opacity = '';
    }

    _initMobileCarousel() {
      this._scrollComplete = true;
      this._navMode = true;

      for (const item of this.items) {
        item.style.cssText = '';
        item.classList.add('nav-transition');
      }
      this._assignNavSlots();
      this._setHandleVisible(true);

      this._bindHandleControl();
      this._bindTouch();
      this._bindKeyboard();
    }

    _bindScroll() {
      const onScroll = () => {
        if (this._rafId) return;
        this._rafId = requestAnimationFrame(() => {
          this._rafId = null;
          this._updateScroll();
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true, signal: this._ac.signal });
      window.addEventListener('resize', () => this._updateScroll(), { passive: true, signal: this._ac.signal });
    }

    _updateScroll() {
      const rect = this.getBoundingClientRect();
      const sectionHeight = this.offsetHeight;
      const viewportH = window.innerHeight;

      const scrollableDistance = sectionHeight - viewportH;
      if (scrollableDistance <= 0) return;

      const scrolled = -rect.top;
      const rawProgress = Math.max(0, Math.min(1, scrolled / scrollableDistance));

      /* Departure clone housekeeping: drop it once it has fully scrolled
       * out of sight above (its job is done), or when the user returns to
       * the end of the triptych (the real, reset stage takes over — nav
       * resumes from the first photo). */
      if (this._departureClone) {
        if (scrolled <= scrollableDistance - viewportH || scrolled >= scrollableDistance) {
          this._removeDepartureClone();
        }
      }

      /* Return-flow handoff (see _onScrollRevert): back to the normal fixed
       * stage at the host TOP — scrolled <= 0 is where the absolute-at-top
       * box and the fixed viewport coincide exactly, so the switch cannot
       * be seen — or at the host BOTTOM when the user re-enters the end
       * (the regular render below immediately paints the end state). The
       * collapsed host height is restored: at the top the host grows below
       * the viewport (invisible); at the bottom the growth happens above
       * the end position, so the scroll is compensated back. */
      if (this._returnFlow && (scrolled <= 0 || scrolled >= scrollableDistance)) {
        const atBottom = scrolled >= scrollableDistance;
        this._returnFlow = false;
        this.classList.remove('is-return-flow');
        if (this._returnHostDelta) {
          const delta = this._returnHostDelta;
          this._returnHostDelta = 0;
          this.style.height = '';
          if (atBottom) window.scrollBy(0, delta);
        }
        if (atBottom) {
          /* The user came back down WITHOUT reaching the top — the reset
           * must not materialise: restore the carousel exactly as they
           * left it (the departing clone showed the picked photo, so the
           * live stage underneath must match when the clone drops this
           * same tick). Slots are applied WITHOUT nav-transition — the
           * class re-arms next frame — so nothing flies; _scrollComplete
           * is set here so the completion hook below can't wipe the
           * restored nav state, and _navigated re-arms the departure flow
           * for the next scroll-up. */
          this.centerIndex = this._returnPickedIndex || 0;
          this._navMode = true;
          this._navigated = true;
          this._scrollComplete = true;
          for (const item of this.items) {
            item.classList.remove('nav-transition');
            item.style.cssText = '';
          }
          this._assignNavSlots();
          this._setHandleVisible(true);
          requestAnimationFrame(() => {
            if (!this._navMode) return;
            for (const item of this.items) item.classList.add('nav-transition');
          });
        } else {
          // Reached the top — the reset stage IS the final state; the
          // remembered pick is no longer meaningful.
          this._returnPickedIndex = 0;
        }
      }

      if (this._integrated) {
        const isPast = scrolled >= scrollableDistance;
        if (isPast) {
          /* The past-state viewport width must equal the FIXED-state box
           * (left:0;right:0 = viewport minus scrollbar). The CSS fallback
           * 100vw INCLUDES the scrollbar, so the fixed→absolute switch
           * widened the box by ~15px and every %-positioned item hopped
           * right at the end of the triptych. clientWidth is exact. */
          const pageWidth = `${document.documentElement.clientWidth}px`;
          if (this._pastPageWidth !== pageWidth) {
            this._pastPageWidth = pageWidth;
            this.style.setProperty('--triptych-page-width', pageWidth);
          }
        }
        this.classList.toggle('is-past', isPast);
        document.body.classList.toggle('triptych-gallery-past', isPast);
      }

      let triptychProgress = rawProgress;

      if (this._integrated) {
        const phase1End = this._getPhase1End();

        if (rawProgress <= phase1End) {
          this.classList.remove('is-phase2');

          if (this._infoWrapper) {
            const infoFadeStart = phase1End * 0.65;
            const infoAlpha = rawProgress < infoFadeStart
              ? 1
              : 1 - ((rawProgress - infoFadeStart) / (phase1End - infoFadeStart));
            this._infoWrapper.style.opacity = Math.max(0, infoAlpha);
            this._infoWrapper.style.pointerEvents = '';
            if (this._breadcrumbSection) {
              this._breadcrumbSection.style.opacity = Math.max(0, infoAlpha);
              this._breadcrumbSection.style.pointerEvents = infoAlpha > 0.05 ? '' : 'none';
            }
          }

          /* Fast scroll can jump straight here from the completed/nav state
           * without ever passing the <0.98 revert window below — run the
           * revert hook now so nav leftovers are cleaned up. */
          if (this._scrollComplete) {
            this._scrollComplete = false;
            this._onScrollRevert();
          }
          if (!this._navMode) this._renderPhase1();
          return;

        } else {
          this.classList.add('is-phase2');
          if (this._infoWrapper) {
            this._infoWrapper.style.opacity = '0';
            this._infoWrapper.style.pointerEvents = 'none';
          }
          if (this._breadcrumbSection) {
            this._breadcrumbSection.style.opacity = '0';
            this._breadcrumbSection.style.pointerEvents = 'none';
          }

          triptychProgress = (rawProgress - phase1End) / (1 - phase1End);
        }
      }

      if (!this._scrollComplete && triptychProgress >= 1) {
        this._scrollComplete = true;
        this._onScrollComplete();
      } else if (this._scrollComplete && triptychProgress < 0.98) {
        this._scrollComplete = false;
        this._onScrollRevert();
      }

      /* Return flow: the stage is un-fixed and shows only the first photo —
       * no morph rendering until a handoff point clears the flag. */
      if (!this._navMode && !this._returnFlow) {
        this._renderAtProgress(triptychProgress);
      }
    }

    _renderPhase1() {
      const center = this._roleMap['center'];
      if (center) {
        Object.assign(center.style, {
          left: '0%', top: '0%', width: '100%', height: '100%',
          opacity: '1', zIndex: '2'
        });
      }
      for (const role of ['left', 'right']) {
        const item = this._roleMap[role];
        if (!item) continue;
        Object.assign(item.style, {
          left: role === 'left' ? '-30%' : '110%',
          opacity: '0'
        });
      }
      this._setHandleVisible(false);
    }

    _getPhase1End() {
      if (!this._infoWrapper) return 0;

      const sectionHeight = this.offsetHeight;
      const viewportH = window.innerHeight;
      const scrollableDistance = sectionHeight - viewportH;
      const infoHeight = this._infoWrapper.offsetHeight;

      return Math.min(0.45, infoHeight / scrollableDistance);
    }

    _renderAtProgress(t) {
      const easedT = easeInOutCubic(t);

      for (const role of ['center', 'left', 'right']) {
        const item = this._roleMap[role];
        if (!item) continue;

        const layout = LAYOUTS[role];
        const s = item.style;
        s.left   = `${lerp(layout.start.left, layout.end.left, easedT)}%`;
        s.top    = `${lerp(layout.start.top, layout.end.top, easedT)}%`;
        s.width  = `${lerp(layout.start.width, layout.end.width, easedT)}%`;
        s.height = `${lerp(layout.start.height, layout.end.height, easedT)}%`;
        s.opacity = lerp(layout.start.opacity, layout.end.opacity, easedT);
        s.zIndex = role === 'center' ? '2' : '1';
      }

      this._setHandleVisible(t >= 0.95);
    }

    _setHandleVisible(visible) {
      this.classList.toggle('handle-visible', visible);
    }

    _onScrollComplete() {
      this._navMode = false;
      // Fresh arrival at the end — no navigation happened yet, so a plain
      // scroll back up replays the reverse morph (see _onScrollRevert).
      this._navigated = false;
      /* AAS-4874: the anchor center is no longer guaranteed to be items[0]
       * (the hero can sit anywhere in DOM order). Align the nav starting
       * point with the item the morph actually centered, so the first
       * _enableNavMode() assigns slots around the visible photo. */
      const anchorIdx = this.items.indexOf(this._roleMap && this._roleMap.center);
      if (anchorIdx >= 0) this.centerIndex = anchorIdx;
    }

    _onScrollRevert() {
      this._navMode = false;

      /* AAS-4874: a revert can land mid-drag or mid-snap, before
       * _snapDragToNearest() commits centerIndex — resolve it from the live
       * drag position so the same-photo check below judges what is actually
       * on screen, not the pre-drag state. */
      if (this._dragActive || this._snapDragRAF) {
        const len = this.items.length;
        this.centerIndex = ((Math.round(this._dragScrollPos) % len) + len) % len;
      }
      if (this._dragRAFId) {
        cancelAnimationFrame(this._dragRAFId);
        this._dragRAFId = null;
      }
      if (this._snapDragRAF) {
        cancelAnimationFrame(this._snapDragRAF);
        this._snapDragRAF = null;
      }
      this._dragActive = false;
      this.isAnimating = false;

      this._removeDragClones();

      /* The user navigated the carousel to ANOTHER photo. Replaying the
       * reverse morph on the original _roleMap trio would make the picked
       * photo vanish and the old set pop in mid-morph. Instead:
       *   1. a static CLONE of the picked trio parks at the host bottom
       *      (same anchoring as the is-past viewport) and scrolls away
       *      downward like ordinary content;
       *   2. the real stage enters RETURN FLOW: un-fixed (absolute at the
       *      host TOP, is-return-flow class), showing only the first photo
       *      in the phase-1 layout — no morph plays underneath, the photo
       *      scrolls in naturally from above like regular page content.
       * The flow hands back to the normal fixed stage at the host top
       * (scrolled <= 0), where the two positions coincide exactly, or at
       * the host bottom when the user returns to the end. */
      /* AAS-4874: keyed on WHERE the user ended up, not on whether they
       * navigated. When the live center is the anchor item again
       * (_roleMap.center — includes a full wrap-around), the whole trio is
       * provably back in the original arrangement: nav slots place
       * neighbours at index±1, the same relation the original roles
       * encode, and the desktop slot positions are numerically identical
       * to the morph's end layout — so replaying the smooth reverse morph
       * is pixel-seamless, exactly like a pure scroll-up. Only a return
       * from a DIFFERENT photo takes the departure-clone flow below. */
      const returnedFromOtherPhoto =
        this._navigated && this.items[this.centerIndex] !== this._roleMap.center;
      this._navigated = false;
      if (returnedFromOtherPhoto) {
        /* Remember the picked photo: scrolling back DOWN before reaching
         * the top must restore the carousel exactly as the user left it —
         * the reset to the first photo only ever materialises at the top. */
        this._returnPickedIndex = this.centerIndex;
        this._spawnDepartureClone();
        this._returnFlow = true;
        this.classList.add('is-return-flow');
        const pageWidth = `${document.documentElement.clientWidth}px`;
        if (this._pastPageWidth !== pageWidth) {
          this._pastPageWidth = pageWidth;
          this.style.setProperty('--triptych-page-width', pageWidth);
        }
        /* Strip the nav transition BEFORE the phase-1 styles land — the
         * collapse below forces a layout (offsetHeight), which commits the
         * new styles while nav-transition would still be on and the hero
         * would visibly FLY from its nav slot into the full-bleed frame.
         * (The is-return-flow CSS also hard-disables item transitions for
         * the whole flow as a belt-and-suspenders.) */
        for (const item of this.items) item.classList.remove('nav-transition');
        this._setHandleVisible(false);
        this._renderPhase1();
        /* Collapse the host to photo + clone (2×100vh, stacked adjacent) so
         * the return trip has no dead blank scroll — the 350vh morph room
         * isn't needed when no morph plays. The scroll position is
         * compensated by the removed amount in the same task: everything
         * anchored to the host bottom (the clone, the sections below) stays
         * visually EXACTLY where it was. */
        const returnHeight = window.innerHeight * 2;
        const removed = this.offsetHeight - returnHeight;
        if (removed > 0) {
          this._returnHostDelta = removed;
          this.style.height = `${returnHeight}px`;
          window.scrollBy(0, -removed);
        }
      }

      // Sync centerIndex to whichever item _roleMap currently marks as center,
      // so the next nav click picks up from the visible position, not items[0].
      const centerItem = this._roleMap && this._roleMap.center;
      const idx = centerItem ? this.items.indexOf(centerItem) : 0;
      this.centerIndex = idx < 0 ? 0 : idx;

      // _roleMap (not data-role attributes) is the source of truth — syncToColor()
      // rewrites _roleMap without touching data-role attributes.
      const roleOf = new Map();
      if (this._roleMap) {
        if (this._roleMap.center) roleOf.set(this._roleMap.center, 'center');
        if (this._roleMap.left)   roleOf.set(this._roleMap.left, 'left');
        if (this._roleMap.right)  roleOf.set(this._roleMap.right, 'right');
      }

      for (const item of this.items) {
        item.classList.remove('nav-transition');

        // Items not in _roleMap won't be repositioned by _renderAtProgress;
        // push them off-screen so stale inline styles don't leave them visible.
        if (!roleOf.has(item)) {
          item.style.cssText = '';
          this._applySlotStyle(item, 'hidden');
        } else if (!returnedFromOtherPhoto) {
          /* AAS-4874: the reverse morph is about to drive this trio — reset
           * it to canonical slot styles first. Nav leaves them there anyway
           * (visual no-op), but a cancelled drag/snap leaves display:none,
           * transition:none and pixel geometry behind, which
           * _renderAtProgress never clears — the morph would animate
           * invisible items. Slot styles equal the morph's end layout, so
           * the first frame is seamless. */
          item.style.cssText = '';
          this._applySlotStyle(item, roleOf.get(item));
        }
      }
    }

    /* Departure clone (see _onScrollRevert): a static snapshot of the
     * picked trio, parked at the host bottom exactly like the is-past
     * viewport (absolute, bottom:0, scrollbar-less page width). Being in
     * normal flow it scrolls away downward with the page, covering — and
     * progressively revealing — the reset stage running the standard
     * reverse morph beneath. z-index 31: one above the phase-2 viewport
     * (z:30), which is the state the revert always leaves from. */
    _spawnDepartureClone() {
      this._removeDepartureClone();

      const viewportH = window.innerHeight;
      const scrollableDistance = this.offsetHeight - viewportH;
      const scrolled = -this.getBoundingClientRect().top;
      // The parked region isn't on screen (fast jump straight into phase 1)
      // — nothing to cover, don't bother.
      if (scrolled <= scrollableDistance - viewportH) return;

      const len = this.items.length;
      const trio = [this.items[this.centerIndex]];
      if (len >= 2) trio.push(this.items[this._wrap(this.centerIndex + 1)]);
      if (len >= 3) trio.push(this.items[this._wrap(this.centerIndex - 1)]);

      const box = document.createElement('div');
      box.setAttribute('aria-hidden', 'true');
      box.style.cssText =
        'position:absolute;left:0;bottom:0;height:100vh;' +
        'width:var(--triptych-page-width,100vw);' +
        'overflow:hidden;z-index:31;pointer-events:none;' +
        'background-color:rgb(var(--color-atomic-white));';
      for (const item of trio) {
        const clone = item.cloneNode(true);
        clone.classList.remove('nav-transition');
        box.appendChild(clone);
      }
      this.appendChild(box);
      this._departureClone = box;
    }

    _removeDepartureClone() {
      if (this._departureClone) {
        this._departureClone.remove();
        this._departureClone = null;
      }
    }

    _wrap(i) {
      const len = this.items.length;
      return ((i % len) + len) % len;
    }

    _enableNavMode() {
      this._navMode = true;
      for (const item of this.items) {
        item.style.cssText = '';
        item.classList.add('nav-transition');
      }
      this._assignNavSlots();
    }

    _assignNavSlots() {
      const len = this.items.length;
      for (const item of this.items) {
        item.dataset.slot = 'hidden';
        this._applySlotStyle(item, 'hidden');
      }
      this.items[this.centerIndex].dataset.slot = 'center';
      this._applySlotStyle(this.items[this.centerIndex], 'center');

      if (len >= 2) {
        this.items[this._wrap(this.centerIndex + 1)].dataset.slot = 'right';
        this._applySlotStyle(this.items[this._wrap(this.centerIndex + 1)], 'right');
      }
      if (len >= 3) {
        this.items[this._wrap(this.centerIndex - 1)].dataset.slot = 'left';
        this._applySlotStyle(this.items[this._wrap(this.centerIndex - 1)], 'left');
      }
    }

    _applySlotStyle(el, slot) {
      const mobile = window.innerWidth < 1025;

      const positions = mobile ? {
        center:      { left: '0%',    top: '0%', width: '100%', height: '100%', opacity: '1', zIndex: '2' },
        left:        { left: '-100%', top: '0%', width: '100%', height: '100%', opacity: '1', zIndex: '1' },
        right:       { left: '100%',  top: '0%', width: '100%', height: '100%', opacity: '1', zIndex: '1' },
        hidden:      { left: '200%',  top: '0%', width: '100%', height: '100%', opacity: '0', zIndex: '0' },
        'off-left':  { left: '-200%', top: '0%', width: '100%', height: '100%', opacity: '0', zIndex: '0' },
        'off-right': { left: '200%',  top: '0%', width: '100%', height: '100%', opacity: '0', zIndex: '0' },
      } : {
        center:      { left: '30%',   top: '5%',    width: '40%', height: '90%', opacity: '1', zIndex: '2' },
        left:        { left: '1.5%',  top: '17.5%', width: '27%', height: '65%', opacity: '1', zIndex: '1' },
        right:       { left: '71.5%', top: '17.5%', width: '27%', height: '65%', opacity: '1', zIndex: '1' },
        hidden:      { left: '110%',  top: '17.5%', width: '27%', height: '65%', opacity: '0', zIndex: '0' },
        'off-left':  { left: '-30%',  top: '17.5%', width: '27%', height: '65%', opacity: '0', zIndex: '0' },
        'off-right': { left: '110%',  top: '17.5%', width: '27%', height: '65%', opacity: '0', zIndex: '0' },
      };

      const pos = positions[slot] || positions.hidden;
      Object.assign(el.style, pos);
    }

    /* AAS-4874: recycled nav (3-slide gallery) — the exiting edge slide is
     * the SAME node that must instantly re-enter on the opposite side, so
     * the real item has to teleport. Animate the exit with a throwaway
     * clone instead: it starts at the item's current slot geometry, slides
     * to the off-stage slot and fades, then removes itself. Reuses the
     * drag system's clone class so its stale-sweep also covers leftovers. */
    _animateRecycledExit(item, exitSlot) {
      const clone = item.cloneNode(true);
      clone.removeAttribute('id');
      clone.classList.add('pdp-gallery-apparel__clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.style.cssText = item.style.cssText;
      clone.classList.add('nav-transition');
      this.stage.appendChild(clone);
      requestAnimationFrame(() => {
        this._applySlotStyle(clone, exitSlot);
        // Above the neighbour gliding into the vacated slot (z:1) — opaque
        // MOD photos would otherwise hide the exit the moment they overlap.
        clone.style.zIndex = '2';
      });
      setTimeout(() => clone.remove(), TRANSITION_MS + 100);
    }

    /* AAS-4874: nav on a 2-slide gallery — both directions are the same
     * animated swap between the only two canonical slots (center, right).
     * Transitions stay on both items the whole time: nothing teleports,
     * nothing blinks out. */
    _swapTwoSlides() {
      const outgoing = this.items[this.centerIndex];
      this.centerIndex = this._wrap(this.centerIndex + 1);
      // AAS-5482: the arrow/drag paths set centerIndex directly and never reach
      // _activateIndex, so the hint has to be re-pointed here as well.
      this._retargetSizes(this.items[this.centerIndex]);
      requestAnimationFrame(() => {
        // Bail if _onScrollRevert() disengaged nav mode before this RAF fired.
        if (!this._navMode) return;
        this._applySlotStyle(this.items[this.centerIndex], 'center');
        this._applySlotStyle(outgoing, 'right');
      });
      setTimeout(() => { this.isAnimating = false; }, TRANSITION_MS + 50);
    }

    _navigateRight() {
      if (this.isAnimating || this._dragActive || this.items.length < 2) return;
      /* The handle fades in at t>=0.95 but the morph completes at t=1 — a
       * click/swipe/arrow in that window engaged nav mode MID-MORPH, which
       * stops the scroll rendering for good (_renderAtProgress is gated on
       * !navMode) and the revert cleanup never runs (_scrollComplete was
       * never set), leaving the triptych wedged in nav slots with the
       * product info fading in over it. Ignore nav until the morph is done;
       * the handle drag path already carries this same guard. */
      if (!this._navMode && !this._scrollComplete) return;
      if (!this._navMode) this._enableNavMode();
      this._navigated = true;
      this.isAnimating = true;

      const len = this.items.length;

      /* AAS-4874: 2-slide gallery (the render-fewer fallback). The generic
       * path below treats the old center as the "incoming" item and
       * teleports it to off-right without a transition — it visibly blinks
       * out. With two items the only canonical arrangement is center+right
       * (_assignNavSlots), so both nav directions are an animated SWAP:
       * center glides to the right slot, the right slide glides to center. */
      if (len === 2) {
        this._swapTwoSlides();
        return;
      }

      const oldLeftIdx = this._wrap(this.centerIndex - 1);
      const newRightIdx = this._wrap(this.centerIndex + 2);
      const recycled = (oldLeftIdx === newRightIdx);

      if (!recycled) {
        this._applySlotStyle(this.items[oldLeftIdx], 'off-left');
        /* AAS-4874: keep the exiting slide ABOVE the old center gliding into
         * the vacated left slot (which lands at z:1, the exiting slot style
         * says z:0). Opaque MOD photos otherwise hide it the moment they
         * overlap — an abrupt vanish instead of the slide-and-fade. The next
         * slot assignment overwrites the z. */
        this.items[oldLeftIdx].style.zIndex = '2';
      } else {
        // AAS-4874: visible exit via clone; the real node teleports across.
        this._animateRecycledExit(this.items[oldLeftIdx], 'off-left');
        this.items[oldLeftIdx].classList.remove('nav-transition');
        this._applySlotStyle(this.items[oldLeftIdx], 'hidden');
        this.items[oldLeftIdx].offsetHeight;
        this.items[oldLeftIdx].classList.add('nav-transition');
      }

      this.centerIndex = this._wrap(this.centerIndex + 1);
      // AAS-5482: the arrow/drag paths set centerIndex directly and never reach
      // _activateIndex, so the hint has to be re-pointed here as well.
      this._retargetSizes(this.items[this.centerIndex]);

      requestAnimationFrame(() => {
        // Bail if _onScrollRevert() disengaged nav mode before this RAF fired.
        if (!this._navMode) return;
        this._applySlotStyle(this.items[this._wrap(this.centerIndex - 1)], 'left');
        this._applySlotStyle(this.items[this.centerIndex], 'center');

        /* Incoming right slide enters from off-stage — identical for the
         * recycled (3-slide) and plain cases; the exit difference is handled
         * above, before the teleport. For the plain case the off-right
         * teleport is a visual no-op (hidden and off-right share geometry). */
        const incoming = this.items[this._wrap(this.centerIndex + 1)];
        incoming.classList.remove('nav-transition');
        this._applySlotStyle(incoming, 'off-right');
        incoming.offsetHeight;
        incoming.classList.add('nav-transition');
        requestAnimationFrame(() => { if (this._navMode) this._applySlotStyle(incoming, 'right'); });
      });

      setTimeout(() => { this.isAnimating = false; }, TRANSITION_MS + 50);
    }

    _navigateLeft() {
      if (this.isAnimating || this._dragActive || this.items.length < 2) return;
      // Morph not finished — see _navigateRight.
      if (!this._navMode && !this._scrollComplete) return;
      if (!this._navMode) this._enableNavMode();
      this._navigated = true;
      this.isAnimating = true;

      const len = this.items.length;

      // AAS-4874: 2-slide gallery — animated swap, see _navigateRight.
      if (len === 2) {
        this._swapTwoSlides();
        return;
      }

      const oldRightIdx = this._wrap(this.centerIndex + 1);
      const newLeftIdx = this._wrap(this.centerIndex - 2);
      const recycled = (oldRightIdx === newLeftIdx);

      if (!recycled) {
        this._applySlotStyle(this.items[oldRightIdx], 'off-right');
        // AAS-4874: exiting slide above the arriving neighbour — see _navigateRight.
        this.items[oldRightIdx].style.zIndex = '2';
      } else {
        // AAS-4874: visible exit via clone; the real node teleports across.
        this._animateRecycledExit(this.items[oldRightIdx], 'off-right');
        this.items[oldRightIdx].classList.remove('nav-transition');
        this._applySlotStyle(this.items[oldRightIdx], 'hidden');
        this.items[oldRightIdx].offsetHeight;
        this.items[oldRightIdx].classList.add('nav-transition');
      }

      this.centerIndex = this._wrap(this.centerIndex - 1);
      // AAS-5482: the arrow/drag paths set centerIndex directly and never reach
      // _activateIndex, so the hint has to be re-pointed here as well.
      this._retargetSizes(this.items[this.centerIndex]);

      requestAnimationFrame(() => {
        // Bail if _onScrollRevert() disengaged nav mode before this RAF fired.
        if (!this._navMode) return;
        this._applySlotStyle(this.items[this._wrap(this.centerIndex + 1)], 'right');
        this._applySlotStyle(this.items[this.centerIndex], 'center');

        // Incoming left slide enters from off-stage — see _navigateRight.
        const incoming = this.items[this._wrap(this.centerIndex - 1)];
        incoming.classList.remove('nav-transition');
        this._applySlotStyle(incoming, 'off-left');
        incoming.offsetHeight;
        incoming.classList.add('nav-transition');
        requestAnimationFrame(() => { if (this._navMode) this._applySlotStyle(incoming, 'left'); });
      });

      setTimeout(() => { this.isAnimating = false; }, TRANSITION_MS + 50);
    }

    _bindHandleControl() {
      /* All element-scoped listeners ride the controller too: the breakpoint
       * re-init (see the matchMedia hook at the bottom) re-runs _init() on
       * the SAME element, and unremovable anonymous listeners would stack —
       * every handle click navigating twice after one resize. */
      const signal = this._ac.signal;
      this.addEventListener('handle:left', () => { this._navigateLeft(); }, { signal });
      this.addEventListener('handle:right', () => { this._navigateRight(); }, { signal });

      this.addEventListener('handle:dragstart', () => {
        if (!this._scrollComplete || this.isAnimating || this._dragActive) return;
        if (!this._navMode) this._enableNavMode();
        this._navigated = true;
        this._startDrag();
      }, { signal });

      this.addEventListener('handle:dragmove', (e) => {
        this._dragHandleDisp = e.detail.displacement;
      }, { signal });

      this.addEventListener('handle:dragend', () => {
        this._stopDrag();
      }, { signal });
    }

    _computeDragSlots() {
      const sw = this.viewport.offsetWidth;
      const sh = this.viewport.offsetHeight;

      if (window.innerWidth < 1025) {
        this._dragSlotDefs = [
          { l: -2 * sw, w: sw,          h: sh,          t: 0 },
          { l: -sw,     w: sw,          h: sh,          t: 0 },
          { l: 0,       w: sw,          h: sh,          t: 0 },
          { l: sw,      w: sw,          h: sh,          t: 0 },
          { l: 2 * sw,  w: sw,          h: sh,          t: 0 }
        ];
        return;
      }

      const sideW = 0.27  * sw;
      const sideH = 0.65  * sh;
      const sideT = 0.175 * sh;
      const ctrW  = 0.40  * sw;
      const ctrH  = 0.90  * sh;
      const ctrT  = 0.05  * sh;

      this._dragSlotDefs = [
        { l: -0.30  * sw, w: sideW, h: sideH, t: sideT },
        { l:  0.015 * sw, w: sideW, h: sideH, t: sideT },
        { l:  0.30  * sw, w: ctrW,  h: ctrH,  t: ctrT  },
        { l:  0.715 * sw, w: sideW, h: sideH, t: sideT },
        { l:  1.10  * sw, w: sideW, h: sideH, t: sideT }
      ];
    }

    _createDragClones() {
      this._removeDragClones();
      this._dragCloneMap = {};
      for (let i = 0; i < this.items.length; i++) {
        const clone = this.items[i].cloneNode(true);
        clone.removeAttribute('id');
        clone.classList.add('pdp-gallery-apparel__clone');
        clone.style.display = 'none';
        clone.setAttribute('aria-hidden', 'true');
        this.stage.appendChild(clone);
        this._dragCloneMap[i] = clone;
      }
    }

    _removeDragClones() {
      if (this._dragCloneMap) {
        for (const k in this._dragCloneMap) {
          this._dragCloneMap[k].remove();
        }
        this._dragCloneMap = null;
      }
      for (const stale of this.stage.querySelectorAll('.pdp-gallery-apparel__clone')) {
        stale.remove();
      }
    }

    _applyDragSlotStyle(el, from, to, f, si) {
      const st = el.style;
      st.display    = '';
      st.position   = 'absolute';
      st.left       = `${lerp(from.l, to.l, f)}px`;
      st.top        = `${lerp(from.t, to.t, f)}px`;
      st.width      = `${lerp(from.w, to.w, f)}px`;
      st.height     = `${lerp(from.h, to.h, f)}px`;
      st.opacity    = '1';
      st.transition = 'none';
      st.zIndex     = (si === 2) ? '2' : '1';
    }

    _renderDragPosition(pos) {
      const len = this.items.length;
      const n   = Math.floor(pos);
      const f   = pos - n;
      const S   = this._dragSlotDefs;

      for (let i = 0; i < len; i++) {
        this.items[i].style.display = 'none';
        if (this._dragCloneMap && this._dragCloneMap[i]) {
          this._dragCloneMap[i].style.display = 'none';
        }
      }

      const placed = {};
      const assignments = [
        { offset: -1, si: 1 },
        { offset:  0, si: 2 },
        { offset:  1, si: 3 },
        { offset:  2, si: 4 }
      ];

      for (const { offset, si } of assignments) {
        const idx  = this._wrap(n + offset);
        const from = S[si];
        const to   = S[si - 1] || from;

        let el;
        if (placed[idx]) {
          el = this._dragCloneMap ? this._dragCloneMap[idx] : null;
          if (!el) continue;
        } else {
          placed[idx] = true;
          el = this.items[idx];
        }

        this._applyDragSlotStyle(el, from, to, f, si);
      }
    }

    _startDrag() {
      if (this._dragActive || this.items.length < 2) return;
      this._dragActive    = true;
      this._dragScrollPos = this.centerIndex;
      this._dragHandleDisp = 0;
      this._dragLastFrame = performance.now();

      if (this._snapDragRAF) {
        cancelAnimationFrame(this._snapDragRAF);
        this._snapDragRAF = null;
      }

      this._computeDragSlots();
      this._createDragClones();
      this._renderDragPosition(this._dragScrollPos);

      const len = this.items.length;
      const frame = (now) => {
        if (!this._dragActive) return;
        const dt = Math.min(0.1, (now - this._dragLastFrame) / 1000);
        this._dragLastFrame = now;

        const d   = this._dragHandleDisp;
        const abs = Math.abs(d) - DEAD_ZONE;
        if (abs > 0) {
          const speed = abs * abs * 0.00025;
          const dir   = d > 0 ? 1 : -1;
          this._dragScrollPos += dir * speed * dt;
          this._dragScrollPos  = ((this._dragScrollPos % len) + len) % len;
        }

        this._renderDragPosition(this._dragScrollPos);
        this._dragRAFId = requestAnimationFrame(frame);
      };
      this._dragRAFId = requestAnimationFrame(frame);
    }

    _stopDrag() {
      if (!this._dragActive) return;
      this._dragActive = false;
      if (this._dragRAFId) {
        cancelAnimationFrame(this._dragRAFId);
        this._dragRAFId = null;
      }
      this._snapDragToNearest();
    }

    _snapDragToNearest() {
      const len = this.items.length;
      const pos = ((this._dragScrollPos % len) + len) % len;
      let target = Math.round(pos);
      if (target >= len) target = 0;

      let diff = target - pos;
      if (diff >  len / 2) diff -= len;
      if (diff < -len / 2) diff += len;

      const start = pos;
      const end   = pos + diff;
      const t0    = performance.now();
      this.isAnimating = true;

      const frame = (now) => {
        const t    = Math.min(1, (now - t0) / SNAP_DURATION);
        const ease = 1 - Math.pow(1 - t, 3);
        const p    = start + (end - start) * ease;
        this._renderDragPosition(((p % len) + len) % len);

        if (t < 1) {
          this._snapDragRAF = requestAnimationFrame(frame);
        } else {
          this.centerIndex = ((target % len) + len) % len;
          this._removeDragClones();

          // Reset inline styles → reflow → re-add transition to avoid a visible jump.
          for (const item of this.items) {
            item.style.cssText = '';
            item.classList.remove('nav-transition');
          }
          this._assignNavSlots();
          this.items[0].offsetHeight;
          for (const item of this.items) {
            item.classList.add('nav-transition');
          }
          this._navMode = true;

          this._snapDragRAF = null;
          this.isAnimating  = false;
        }
      };
      this._snapDragRAF = requestAnimationFrame(frame);
    }

    _bindTouch() {
      const signal = this._ac.signal;
      let startX = 0, startY = 0, swiping = false;
      this.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX; startY = e.touches[0].clientY; swiping = true;
      }, { passive: true, signal });
      this.addEventListener('touchmove', (e) => {
        if (!swiping || e.touches.length !== 1) return;
        const dx = e.touches[0].clientX - startX, dy = e.touches[0].clientY - startY;
        if (Math.abs(dy) > Math.abs(dx)) { swiping = false; return; }
        if (Math.abs(dx) > 40) { swiping = false; dx < 0 ? this._navigateRight() : this._navigateLeft(); }
      }, { passive: true, signal });
      this.addEventListener('touchend', () => { swiping = false; }, { passive: true, signal });
    }

    /* Arrow keys mirror the on-screen handle, so they act ONLY while that
     * handle is actually usable. The original gate ("gallery not fully
     * off-screen") was always true for the fixed fullscreen gallery, so
     * arrows anywhere on the page hijacked it — including mid scroll
     * choreography, which corrupted the triptych state. Three guards:
     *   1. handle-visible — the class that fades the handle in (t >= 0.95,
     *      i.e. nav mode; scrolling back up removes it);
     *   2. the handle is inside the viewport — is-past parks the gallery at
     *      its container bottom, so far below it the handle is off-screen
     *      while handle-visible still holds;
     *   3. the fullscreen overlay is closed (pdp-apparel-overlay-lock) —
     *      its own ArrowLeft/Right handler drives the lightbox, and both
     *      firing at once would double-navigate. */
    _bindKeyboard() {
      this._keyHandler = (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        if (!this.classList.contains('handle-visible')) return;
        if (!this.handleEl) return;
        const rect = this.handleEl.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
        if (document.body.classList.contains('pdp-apparel-overlay-lock')) return;
        e.preventDefault();
        if (e.key === 'ArrowLeft') this._navigateLeft();
        else this._navigateRight();
      };
      document.addEventListener('keydown', this._keyHandler, { signal: this._ac.signal });
    }

    syncToColor(handle) {
      if (!handle || !this.items || !this.items.length) return;

      let idx = this.items.findIndex(item => item.dataset.color === handle);
      if (idx === -1) {
        const pattern = `color-${handle}`;
        idx = this.items.findIndex(item => {
          const img = item.querySelector('img');
          return img && (img.getAttribute('src') || '').includes(pattern);
        });
      }
      if (idx === -1) return;

      this._activateIndex(idx);
    }

    syncToMedia(mediaId) {
      if (mediaId == null || !this.items || !this.items.length) return;
      const id = String(mediaId);
      const idx = this.items.findIndex(item => item.dataset.mediaId === id);
      if (idx === -1) return;
      this._activateIndex(idx);
    }

    /* AAS-5482: re-point the incoming centre's `sizes` at the full viewport.
     *
     * Every slide is rendered once with a static hint describing the position it
     * happened to start in — a side tile claims a fraction of the viewport, the
     * centre claims all of it. Navigation moves a side tile INTO the centre,
     * where it paints full-bleed, but nothing used to touch the markup: the
     * browser kept the candidate it had picked for a narrow slot and upscaled it
     * across the whole screen. Measured on a Retina laptop, a side slide holds
     * 378px where the centre needs 3024.
     *
     * Rewriting sizes makes the browser re-run its own selection and pull a
     * larger candidate. Deliberately a fetch at navigation time rather than up
     * front: pre-loading every slide at centre size would undo the saving the
     * honest side hint buys on first paint. The old candidate stays painted
     * until the new one decodes, so the swap shows a briefly softer frame rather
     * than an empty one.
     *
     * Called before the animation starts, so the request is already in flight
     * while the tile travels. The resting hint is kept on the element so a tile
     * leaving the centre goes back to asking for what it actually occupies. */
    _retargetSizes(centerItem) {
      if (!centerItem) return;
      for (const item of this.items) {
        const img = item.querySelector('img');
        if (!img) continue;
        if (img.dataset.restingSizes === undefined) {
          img.dataset.restingSizes = img.getAttribute('sizes') || '';
        }
        const wanted = item === centerItem ? '100vw' : img.dataset.restingSizes;
        if (wanted && img.getAttribute('sizes') !== wanted) img.setAttribute('sizes', wanted);
      }
    }

    _activateIndex(idx) {
      this.centerIndex = idx;

      // _roleMap is the single source of truth for triptych positions (_onScrollRevert relies on it).
      this._roleMap['center'] = this.items[idx];
      this._roleMap['right']  = this.items[this._wrap(idx + 1)];
      this._roleMap['left']   = this.items[this._wrap(idx - 1)];

      this._retargetSizes(this._roleMap['center']);

      if (this._scrollComplete || this._navMode) {
        this._enableNavMode();
      } else {
        for (const item of this.items) {
          item.style.left    = '110%';
          item.style.opacity = '0';
        }
        this._renderPhase1();
      }
    }

    /* Full state reset for the desktop<->mobile breakpoint re-init (the
     * matchMedia hook below): the two modes were only ever initialised once
     * per element, so crossing 1025px live left the wrong choreography
     * running — mobile->desktop had NO scroll morph at all (and the hero
     * photo filtered out of items for good), desktop->mobile kept the
     * desktop morph driving a mobile layout. Kill everything the current
     * mode owns, then _init() rebuilds cleanly for the new width. */
    _teardown() {
      if (this._ac) this._ac.abort();
      if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null; }
      if (this._dragRAFId) { cancelAnimationFrame(this._dragRAFId); this._dragRAFId = null; }
      if (this._snapDragRAF) { cancelAnimationFrame(this._snapDragRAF); this._snapDragRAF = null; }
      if (this.stage) this._removeDragClones();
      this._removeDepartureClone();
      this._navMode = false;
      this._navigated = false;
      this._scrollComplete = false;
      this._returnFlow = false;
      this._returnHostDelta = 0;
      this._returnPickedIndex = 0;
      this._dragActive = false;
      this.isAnimating = false;
      this._pastPageWidth = null;
      this.style.height = '';
      this.classList.remove('is-phase2', 'is-past', 'is-return-flow', 'handle-visible');
      for (const item of this.querySelectorAll('.pdp-gallery-apparel__item')) {
        item.classList.remove('nav-transition');
        item.style.cssText = '';
        delete item.dataset.slot;
      }
      if (this._infoWrapper) {
        this._infoWrapper.style.opacity = '';
        this._infoWrapper.style.pointerEvents = '';
      }
      if (this._breadcrumbSection) {
        this._breadcrumbSection.style.opacity = '';
        this._breadcrumbSection.style.pointerEvents = '';
      }
      // _init() re-adds these for the integrated desktop mode; mobile (like a
      // page loaded mobile-first) runs without them.
      document.body.classList.remove('has-triptych-gallery', 'triptych-gallery-past');
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      if (this._dragRAFId) cancelAnimationFrame(this._dragRAFId);
      if (this._snapDragRAF) cancelAnimationFrame(this._snapDragRAF);
      this._removeDragClones();
      this._removeDepartureClone();
      /* Variant swaps replace this element via an innerHTML rewrite of
       * #ProductMedia, and Chrome runs the OLD element's disconnect AFTER
       * the fresh element's connect (measured: the body classList sequence
       * is ADD -> REMOVE). An unconditional remove here therefore wiped the
       * classes the successor had just set — overflow-x:clip and
       * overflow-anchor:none silently died on the first colour switch, and
       * without overflow-anchor:none the return flow's height collapse +
       * scrollBy compensation got compensated a SECOND time by native
       * scroll anchoring: the page jumped and the revert/restore handoffs
       * wedged the gallery in a fixed nav state. Clean up only when no
       * successor gallery is connected (real page teardown). */
      if (!document.querySelector('pdp-gallery-apparel')) {
        document.body.classList.remove('has-triptych-gallery', 'triptych-gallery-past');
      }
    }
  }

  if (!customElements.get('pdp-gallery-apparel')) {
    customElements.define('pdp-gallery-apparel', PdpGalleryApparel);
  }

  /* Re-init when the desktop breakpoint (1025px, matching the
   * `window.innerWidth < 1025` checks above) is crossed live — DevTools
   * responsive mode, window snapping. Same pattern as product-about.js:
   * without it the element kept the mode it was born with. */
  window.matchMedia('(min-width: 1025px)').addEventListener('change', () => {
    for (const el of document.querySelectorAll('pdp-gallery-apparel')) {
      if (typeof el._teardown !== 'function') continue;
      el._teardown();
      el._init();
    }
  });

})();
